import MapModel                    from '@models/map/MapModel';
import Form                        from '@widesk-ui/components/form/Form';
import Input                       from '@widesk-ui/components/form/Input';
import Select                      from '@widesk-ui/components/form/Select';
import View                        from '@widesk-ui/components/View';
import ColorPicker                 from '@widesk-ui/components/form/ColorPicker';
import React                       from 'react';
import { CheckOutlined }           from '@ant-design/icons';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { MinusOutlined }           from '@ant-design/icons';
import { PlusOutlined }            from '@ant-design/icons';
import useDesignTokens             from '@/hooks/useDesignTokens';
import currentProjectEditorStore   from '@/TokamapEditor/stores/currentProjectEditorStore';
import { loadCurrentDocuments }    from '@/TokamapEditor/stores/PreviewMapsStore';
import { documentOfMap }           from '@/TokamapEditor/stores/pairMapsWithDocuments';
import nextFreeLabel               from '@/tools/nextFreeLabel';
import { AddressSearchResult }     from '@tokamap/core';
import { DEFAULT_FRAMING }         from '@tokamap/core';
import { lngLatToMercator }        from '@tokamap/core';
import { serializeFraming }        from '@tokamap/core';
import { message }                 from '@widesk-ui/hooks/useMessage';
import PlaceSearchField            from '@forms/map/PlaceSearchField';
import { LocatedMapIllustration }  from '@forms/map/MapKindIllustrations';
import { FreePlanIllustration }    from '@forms/map/MapKindIllustrations';
import { DEFAULT_PLAN_UNIT }       from '@/TokamapEditor/helpers/mapKind';
import { defaultGridStep }         from '@/TokamapEditor/helpers/mapKind';
import { LENGTH_UNITS }            from '@/TokamapEditor/helpers/mapKind';
import { lengthUnitOf }            from '@/TokamapEditor/helpers/mapKind';
import { mapKindOf }               from '@/TokamapEditor/helpers/mapKind';
import type { LengthUnit }         from '@/TokamapEditor/helpers/mapKind';
import type { MapKind }            from '@/TokamapEditor/helpers/mapKind';

const LOCATED_MAP_ZOOM = 17;
const FREE_PLAN_ZOOM = 20;
const FREE_PLAN_ORIGIN: [number, number] = [0, 0];

const COPIED_VIEW_KEYS = ['center', 'zoom', 'zoomMin', 'zoomMax', 'rotation', 'framing', 'allowPitch'] as const;
const COPIED_BACKGROUND_KEYS = ['tileLayerName', 'osmOptions', 'backgroundColor'] as const;

type RawConfiguration = Record<string, unknown>;

type SourceMap = {
	id: id;
	label: string;
	kind: MapKind;
	unit: LengthUnit;
	configuration: RawConfiguration;
};

export type MapFormProps = {
	model: MapModel;
	onSuccess?: () => void;
	projectUrn?: Urn;
}

export default function MapForm(props: MapFormProps) {
	const isNew = !props.model.id;

	const [kind, setKind] = React.useState<MapKind>('located');
	const [place, setPlace] = React.useState<AddressSearchResult | null>(null);
	const [backgroundColor, setBackgroundColor] = React.useState('#FFFFFF');
	const [unit, setUnit] = React.useState<LengthUnit>(DEFAULT_PLAN_UNIT);
	const [sourceId, setSourceId] = React.useState<id | null>(null);
	const sourceMaps = useSourceMaps(isNew);

	const source = sourceMaps.find(map => map.id === sourceId && map.kind === kind) ?? null;

	const generatedLabel = React.useRef(nextFreeLabel(
		MapModel.localize(),
		currentProjectEditorStore.get.mapCollection.map(m => m.label).filter(Boolean),
		{ numberFirst: true },
	)).current;

	const selectKind = (next: MapKind) => {
		setKind(next);
		setSourceId(null);
	};

	const selectSource = (map: SourceMap | null) => {
		setSourceId(map?.id ?? null);
		if (map?.kind === 'plan') setUnit(map.unit);
	};

	return (
		<Form
			updateOnChange
			onSuccess={props.onSuccess}
			onDidMount={async formTools => {
				if (props.model.id) formTools.setFieldsValue({ label: props.model.label });
			}}
			fields={{
				label: {
					hidden: isNew,
					label: 'Titre',
					element: <Input autoFocus />,
					required: true,
					span: 24,
				},
			}}
			onSubmit={async (values) => {
				const { model } = props;

				if (isNew && kind === 'located' && !source && !place) {
					message.error('Choisissez le lieu de la carte, ou une carte existante dont reprendre le cadrage.');
					throw new Error('Lieu de la carte manquant');
				}

				await model.patch({
					label: values.label ?? generatedLabel,
					project: props.projectUrn,

					...(isNew ? {
						position: currentProjectEditorStore.get.mapCollection.length,
						configuration: newMapConfiguration({ kind, place, backgroundColor, unit, source }),
					} : {}),
				});

				return model;
			}}
		>
			{isNew && (
				<View gap="md">
					<View bold>Que voulez-vous dessiner ?</View>

					<View row gap="md" style={{ flexWrap: 'wrap' }}>
						<KindTile
							selected={kind === 'located'}
							onSelect={() => selectKind('located')}
							illustration={<LocatedMapIllustration />}
							title="Une carte située"
							unit="m"
							hint="Un site, un bâtiment, un événement posé sur la carte du monde, à son vrai emplacement."
							features={[
								{ label: 'Fond de carte du monde et ses styles' },
								{ label: 'Reprise des bâtiments existants à la baguette' },
								{ label: 'Géolocalisation des visiteurs' },
							]}
						/>

						<KindTile
							selected={kind === 'plan'}
							onSelect={() => selectKind('plan')}
							illustration={<FreePlanIllustration unit={unit} />}
							title="Un plan libre"
							unit={unit}
							hint="Un dessin coté, sans emplacement réel : un stand, une salle, un aménagement intérieur."
							features={[
								{ label: 'Unité au choix : mm, cm ou m' },
								{ label: 'Règles graduées et grille magnétique' },
								{ label: 'Pas de carte du monde ni de géolocalisation', off: true },
							]}
						/>
					</View>

					<KindSettings
						kind={kind}
						source={source}
						place={place}
						onPlaceChange={setPlace}
						unit={unit}
						onUnitChange={setUnit}
						backgroundColor={backgroundColor}
						onBackgroundColorChange={setBackgroundColor}
					/>

					{!!sourceMaps.length && (
						<FramingSourcePicker
							kind={kind}
							sourceMaps={sourceMaps}
							source={source}
							onSelect={selectSource}
						/>
					)}

					<KindNote kind={kind} />
				</View>
			)}
		</Form>
	);
}

function newMapConfiguration({ kind, place, backgroundColor, unit, source }: {
	kind: MapKind;
	place: AddressSearchResult | null;
	backgroundColor: string;
	unit: LengthUnit;
	source: SourceMap | null;
}): RawConfiguration {
	const base: RawConfiguration = {
		rotation: 0,
		formatVersion: 2,
		framing: serializeFraming(DEFAULT_FRAMING),
		kind,
		unit: kind === 'plan' ? unit : 'm',
		grid: kind === 'plan' ? { step: defaultGridStep(unit) } : null,
	};

	const startView: RawConfiguration = kind === 'located'
		? { center: place ? lngLatToMercator(place.center) : undefined, zoom: LOCATED_MAP_ZOOM, tileLayerName: 'osm' }
		: { center: FREE_PLAN_ORIGIN, zoom: FREE_PLAN_ZOOM, tileLayerName: 'unset', backgroundColor };

	if (!source) return { ...base, ...startView };

	return {
		...base,
		...startView,
		...pick(source.configuration, COPIED_VIEW_KEYS),
		...(kind === 'located' ? pick(source.configuration, COPIED_BACKGROUND_KEYS) : {}),
	};
}

function pick(configuration: RawConfiguration, keys: readonly string[]): RawConfiguration {
	return Object.fromEntries(keys.filter(key => configuration[key] !== undefined).map(key => [key, configuration[key]]));
}

function useSourceMaps(enabled: boolean): SourceMap[] {
	const [sourceMaps, setSourceMaps] = React.useState<SourceMap[]>([]);

	React.useEffect(() => {
		const maps = currentProjectEditorStore.get.mapCollection.models.slice();
		if (!enabled || !maps.length) return;

		let cancelled = false;

		loadCurrentDocuments()
			.then(({ maps: documents }) => {
				if (cancelled) return;

				setSourceMaps(maps.flatMap(map => {
					const configuration = (documentOfMap(documents, map.id) as { configurations?: RawConfiguration; } | undefined)?.configurations;
					if (!configuration?.center) return [];

					return [{
						id: map.id,
						label: map.label,
						kind: mapKindOf(configuration),
						unit: lengthUnitOf(configuration),
						configuration,
					}];
				}));
			})
			.catch(error => console.error('Cartes du projet indisponibles pour la reprise du cadrage.', error));

		return () => {
			cancelled = true;
		};
	}, [enabled]);

	return sourceMaps;
}

const KindTile = (props: {
	selected: boolean;
	onSelect: () => void;
	illustration: React.ReactNode;
	title: string;
	unit: string;
	hint: string;
	features: { label: string; off?: boolean; }[];
}) => {
	const tokens = useDesignTokens();

	return (
		<button
			type="button"
			aria-pressed={props.selected}
			onClick={props.onSelect}
			style={{
				flex: '1 1 240px',
				padding: 0,
				textAlign: 'left',
				font: 'inherit',
				color: 'inherit',
				cursor: 'pointer',
				overflow: 'hidden',
				borderRadius: 10,
				background: tokens.surface.card,
				border: `1.5px solid ${props.selected ? tokens.color.primary : tokens.border.default}`,
				boxShadow: props.selected ? `0 0 0 3px color-mix(in srgb, ${tokens.color.primary} 18%, transparent)` : undefined,
				transition: 'border-color .15s, box-shadow .15s',
			}}
		>
			{props.illustration}

			<View gap="xs" p="md">
				<View row centerV gap="sm">
					<View bold size="large">{props.title}</View>

					<View
						size="small"
						br={99}
						color={props.selected ? tokens.color.primary : tokens.text.muted}
						bg={props.selected ? `color-mix(in srgb, ${tokens.color.primary} 10%, ${tokens.surface.card})` : tokens.surface.subtle}
						style={{ marginLeft: 'auto', padding: '0 8px' }}
					>
						{props.unit}
					</View>
				</View>

				<View size="small" color={tokens.text.muted} lineHeight={1.4}>{props.hint}</View>

				<View gap={2} pt="xs">
					{props.features.map(feature => (
						<View key={feature.label} row centerV gap="sm" size="small" color={feature.off ? tokens.text.muted : undefined}>
							{feature.off
								? <MinusOutlined style={{ color: tokens.text.disabled }} />
								: <CheckOutlined style={{ color: tokens.color.success }} />}
							{feature.label}
						</View>
					))}
				</View>
			</View>
		</button>
	);
};

const KindSettings = (props: {
	kind: MapKind;
	source: SourceMap | null;
	place: AddressSearchResult | null;
	onPlaceChange: (place: AddressSearchResult | null) => void;
	unit: LengthUnit;
	onUnitChange: (unit: LengthUnit) => void;
	backgroundColor: string;
	onBackgroundColorChange: (color: string) => void;
}) => {
	const tokens = useDesignTokens();

	return (
		<View row gap="md" p="md" br={10} bg={tokens.surface.subtle} style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
			{props.kind === 'located' && (
				<LabeledSetting label="Où se trouve-t-elle ?" grow>
					{props.source
						? <View size="small" color={tokens.text.muted}>Au même endroit que « {props.source.label} ».</View>
						: <PlaceSearchField value={props.place} onChange={props.onPlaceChange} />}
				</LabeledSetting>
			)}

			{props.kind === 'plan' && (
				<>
					<LabeledSetting label="Unité de mesure">
						<Select
							value={props.unit}
							onChange={value => props.onUnitChange(value as LengthUnit)}
							options={LENGTH_UNITS}
							style={{ width: 160 }}
						/>
					</LabeledSetting>

					<LabeledSetting label="Couleur de fond">
						<ColorPicker
							showText
							value={props.backgroundColor}
							onChangeComplete={color => props.onBackgroundColorChange(color || '#FFFFFF')}
						/>
					</LabeledSetting>
				</>
			)}
		</View>
	);
};

const LabeledSetting = (props: { label: string; grow?: boolean; children: React.ReactNode; }) => {
	const tokens = useDesignTokens();

	return (
		<View gap={4} flex={props.grow} style={{ minWidth: 0 }}>
			<View size="small" bold color={tokens.text.muted}>{props.label}</View>

			{props.children}
		</View>
	);
};

const FramingSourcePicker = (props: {
	kind: MapKind;
	sourceMaps: SourceMap[];
	source: SourceMap | null;
	onSelect: (map: SourceMap | null) => void;
}) => {
	const tokens = useDesignTokens();

	return (
		<View gap="sm">
			<View row gap="sm" style={{ flexWrap: 'wrap', alignItems: 'baseline' }}>
				<View bold size="small">Cadrage</View>

				<View size="small" color={tokens.text.muted}>
					{props.kind === 'located'
						? 'Partir d\'une carte existante reprend son lieu, son cadrage et son orientation.'
						: 'Partir d\'un plan existant reprend son cadrage. Seuls les plans libres sont proposés.'}
				</View>
			</View>

			<View row gap="sm" style={{ flexWrap: 'wrap' }}>
				<SourceChip
					selected={!props.source}
					onSelect={() => props.onSelect(null)}
					thumbnail={<PlusOutlined style={{ fontSize: 11, color: tokens.text.muted }} />}
					label={props.kind === 'located' ? 'Nouveau lieu' : 'Cadrage par défaut'}
				/>

				{props.sourceMaps.map(map => (
					<SourceChip
						key={map.id}
						selected={props.source?.id === map.id}
						disabled={map.kind !== props.kind}
						onSelect={() => props.onSelect(map)}
						thumbnail={<KindThumbnail kind={map.kind} />}
						label={map.label}
						detail={map.kind === 'located' ? 'carte située' : 'plan libre'}
					/>
				))}
			</View>
		</View>
	);
};

const SourceChip = (props: {
	selected: boolean;
	disabled?: boolean;
	onSelect: () => void;
	thumbnail: React.ReactNode;
	label: string;
	detail?: string;
}) => {
	const tokens = useDesignTokens();

	return (
		<button
			type="button"
			disabled={props.disabled}
			aria-pressed={props.selected}
			onClick={props.onSelect}
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 8,
				padding: '5px 10px 5px 5px',
				textAlign: 'left',
				font: 'inherit',
				color: 'inherit',
				borderRadius: 8,
				opacity: props.disabled ? 0.45 : 1,
				cursor: props.disabled ? 'not-allowed' : 'pointer',
				background: props.selected ? `color-mix(in srgb, ${tokens.color.primary} 10%, ${tokens.surface.card})` : tokens.surface.card,
				border: `1.5px solid ${props.selected ? tokens.color.primary : tokens.border.default}`,
			}}
		>
			<View w={34} h={24} br={4} center bg={tokens.surface.subtle} style={{ overflow: 'hidden', flexShrink: 0, border: `1px solid ${tokens.border.default}` }}>
				{props.thumbnail}
			</View>

			<View>
				<View size="small">{props.label}</View>
				{props.detail && <View size="small" color={tokens.text.muted} style={{ fontSize: 11 }}>{props.detail}</View>}
			</View>
		</button>
	);
};

const KindThumbnail = ({ kind }: { kind: MapKind; }) => {
	const tokens = useDesignTokens();

	const background = kind === 'located'
		? `linear-gradient(160deg, color-mix(in srgb, ${tokens.color.success} 15%, ${tokens.surface.card}) 55%, color-mix(in srgb, ${tokens.color.primary} 25%, ${tokens.surface.card}) 55%)`
		: `linear-gradient(color-mix(in srgb, ${tokens.color.primary} 20%, transparent) 1px, transparent 1px) 0 0 / 6px 6px, linear-gradient(90deg, color-mix(in srgb, ${tokens.color.primary} 20%, transparent) 1px, transparent 1px) 0 0 / 6px 6px, ${tokens.surface.card}`;

	return <View w="100%" h="100%" style={{ background }} />;
};

const KindNote = ({ kind }: { kind: MapKind; }) => {
	const tokens = useDesignTokens();

	return (
		<View
			row
			gap="sm"
			p="sm"
			br={8}
			size="small"
			lineHeight={1.4}
			bg={`color-mix(in srgb, ${tokens.color.warning} 10%, ${tokens.surface.card})`}
			style={{ border: `1px solid color-mix(in srgb, ${tokens.color.warning} 35%, ${tokens.border.default})` }}
		>
			<ExclamationCircleOutlined style={{ color: tokens.color.warning, marginTop: 3 }} />

			<View>
				<View bold>Ce choix est définitif.</View>

				{kind === 'located'
					? 'Une carte située ne pourra pas devenir un plan libre : ses formes sont posées sur le monde.'
					: 'Un plan libre ne pourra pas être posé sur la carte du monde. Son unité, elle, se change à tout moment dans les réglages.'}
			</View>
		</View>
	);
};
